import {create} from 'zustand';
import {
  AddJournalParams,
  BaseMercuryRequestParams,
  JournalRow,
  MercuryState,
  TCountry,
  TEnterprise,
  TMercuryError,
  TransactionsRow,
  TTransactionStatus,
} from './types';
import {useUserStore} from 'entities/user';
import {$api} from 'shared/api';
import {toast} from 'react-toastify';
import {getDigFromString} from 'shared/helpers';
import {showMercuryError} from '../helpers/fns';
import {
  COUNTRY_AUTO_NUMBERS,
  DEFAULT_ENTERPRISE,
  DEFAULT_ENTERPRISE_GUID,
  DEFAULT_STOCK_RESULT,
  DEFAULT_VET_RESULT,
  MERCURY_PACKAGE_TYPES,
  STORAGE_METHODS,
  TRANSPORT_TYPES,
  VET_DOCUMENT_ITEMS,
  WAYBILL_TYPES,
} from '../helpers/consts';
import {StockEntry} from './journalTypes';
import {GettedVetDocument} from './transactionTypes';
import {useVetisStore} from 'entities/vetis';
import moment from 'moment-timezone';

const mercuryCache = new Map();

export const useMercuryStore = create<MercuryState>((set, get) => ({
  journalRowsAll: [],
  journalRows: [],
  transactionsRowsAll: [],
  transactionsRows: [],
  filterJournalParams: {},
  filterTransactionParams: {},
  isJournalLoading: true,
  isTransactionLoading: true,
  setErrors: true,

  enterpriseList: [],
  enterprise: DEFAULT_ENTERPRISE,
  defaultEnterprise: DEFAULT_ENTERPRISE,
  defaultEnterpriseGuid: DEFAULT_ENTERPRISE_GUID,
  packageTypes: MERCURY_PACKAGE_TYPES,
  productTypes: [],
  productItems: [],
  currentUnit: null,
  purposes: [],
  vetDocumentItems: VET_DOCUMENT_ITEMS,
  getJournalEntries: async () => {
    set({
      isJournalLoading: true,
    });
    const {
      filterJournalParams,
      enterpriseList,
      businessEntities,
      productItems,
      setErrors,
      defaultEnterpriseGuid,
      filterJournal,
      checkApplicationStatus,
    } = get();

    const path = '/fgis-soap-native/fgis/api/v1/mercury/getStockEntryList';
    const params: BaseMercuryRequestParams = {
      listOptions: {
        count: '999',
        offset: '0',
      },
      enterpriseGuid: defaultEnterpriseGuid,
    };

    let res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;
    res = await checkApplicationStatus(res);
    console.log('getJournalEntries', res);

    // const result = res?.data?.application?.result;
    const result = res?.data?.application?.result || DEFAULT_STOCK_RESULT;
    const errors = res?.data?.application?.errors;

    if (errors?.length && setErrors) {
      for (let index = 0; index < errors.length; index++) {
        const error: TMercuryError = errors[index];
        if (!error.value.trim()) continue;

        toast.error(error.value);
      }
    }

    const stockEntries: StockEntry[] = result?.content.stockEntryList.stockEntry;
    if (stockEntries) {
      const newEnterpriseList = enterpriseList;
      const newBusinessEntitiesList = businessEntities;
      const newProductItems = productItems;

      const changesRes = await checkApplicationStatus(
        await $api
          .post('/fgis-soap-native/fgis/api/v1/mercury/getStockEntryChangesList', {
            listOptions: {
              count: '999',
              offset: '0',
            },
            businessMember: {
              enterprise: defaultEnterpriseGuid,
            },
            updateDateInterval: {
              beginDate: new Date(0),
            },
            enterpriseGuid: defaultEnterpriseGuid,
          })
          .catch(console.warn)
      );

      const journalRowsAll: JournalRow[] = [];
      for (let index = 0; index < stockEntries.length; index++) {
        const stockEntry = stockEntries[index];
        const number = stockEntry?.entryNumber || '';
        const guid = stockEntry.guid;
        const uuid = stockEntry.uuid;

        const addDate = new Date(stockEntry.createDate);

        let productionDate: Date | undefined;
        const dates = stockEntry.batch?.dateOfProduction?.firstDate;
        if (dates) {
          const {year, month, day, hour} = dates;
          productionDate = new Date(year, month - 1, day || 0, hour || 0);
          if (!year) productionDate = undefined;
        }

        let creator = '';
        const cachedCreator = mercuryCache.has(`creator_${stockEntry.batch.owner.guid}`)
          ? mercuryCache.get(`creator_${stockEntry.batch.owner.guid}`)
          : null;
        if (cachedCreator) {
          creator = cachedCreator.guid;
          if (
            cachedCreator.guid &&
            !newBusinessEntitiesList.find((el) => el.guid === cachedCreator.guid)
          ) {
            newBusinessEntitiesList.push(cachedCreator);
          }
        } else {
          const creatorRes = await $api
            .post('/fgis-soap-native/fgis/api/v1/mercury/enterprise/getBusinessEntityByGuid', {
              guid: stockEntry.batch.owner.guid,
            })
            .catch(console.warn);
          if (
            creatorRes?.data?.businessEntity?.guid &&
            !newBusinessEntitiesList.find((el) => el.guid === creatorRes.data.businessEntity.guid)
          ) {
            newBusinessEntitiesList.push(creatorRes.data.businessEntity);
          }
          if (creatorRes?.data?.businessEntity) {
            mercuryCache.set(
              `creator_${stockEntry.batch.owner.guid}`,
              creatorRes.data.businessEntity
            );
          }
          creator = creatorRes?.data?.businessEntity?.guid;
        }

        let type = '';
        const entrieSubProductGuid = stockEntry.batch.subProduct.guid;
        const cachedType = mercuryCache.has(`subProductRes_${entrieSubProductGuid}`)
          ? mercuryCache.get(`subProductRes_${entrieSubProductGuid}`)
          : null;
        if (cachedType) {
          type = cachedType;
        } else {
          const subProductRes = await $api
            .post('/fgis-soap-native/fgis/api/v1/mercury/product/getSubProductByGuid', {
              guid: entrieSubProductGuid,
            })
            .catch(console.warn);
          if (subProductRes?.data?.subProduct?.guid) {
            type = subProductRes.data.subProduct.guid;
            mercuryCache.set(
              `subProductRes_${entrieSubProductGuid}`,
              subProductRes.data.subProduct.guid
            );
          }
        }

        let productItem = '';
        const entrieProductItemGuid = stockEntry.batch.productItem?.guid;
        if (entrieProductItemGuid) {
          const productItemCached = mercuryCache.has(`productItem_${entrieProductItemGuid}`)
            ? mercuryCache.get(`productItem_${entrieProductItemGuid}`)
            : null;
          if (productItemCached) {
            productItem = productItemCached.guid;
            if (!newProductItems.find((el) => el.guid === productItemCached.guid)) {
              newProductItems.push(productItemCached);
            }
          } else {
            const productItemRes = await $api
              .post('/fgis-soap-native/fgis/api/v1/mercury/product/getProductItemByGuid', {
                guid: entrieProductItemGuid,
              })
              .catch(console.warn);
            if (productItemRes?.data?.productItem?.guid) {
              productItem = productItemRes.data.productItem.guid;
              if (!newProductItems.find((el) => el.guid === productItemRes.data.productItem.guid)) {
                newProductItems.push(productItemRes.data.productItem);
              }
              mercuryCache.set(
                `productItem_${entrieProductItemGuid}`,
                productItemRes.data.productItem
              );
            }
          }
        }

        let remainder = `${stockEntry.batch.volume}`;
        let volume = `${stockEntry.batch.volume}`;
        let unit = '21ed96c9-337b-4a27-8761-c6e6ad3c9f5b';
        if (changesRes) {
          const arr = changesRes.data.application.result.content.stockEntryList.stockEntry
            .filter((el: any) => el.entryNumber === number)
            .sort((a: any, b: any) => {
              const aDate = new Date(a.updateDate);
              const bDate = new Date(b.updateDate);

              return aDate.getTime() - bDate.getTime();
            });

          if (arr[0]?.batch?.volume) {
            volume = `${arr[0].batch.volume} ${arr[0].batch.unit?.name || ''}`;
          }
        }

        if (stockEntry?.batch?.unit?.name) {
          remainder += ` ${stockEntry?.batch?.unit?.name}`;
        } else if (stockEntry?.batch?.unit?.guid) {
          unit = stockEntry.batch.unit.guid;
          const cachedUnit = mercuryCache.get(`unit_${stockEntry.batch.unit.guid}`);
          if (cachedUnit) {
            remainder += ` ${cachedUnit}`;
          } else {
            const unitRes = await $api
              .post('/fgis-soap-native/fgis/api/v1/mercury/dictionary/getUnitByGuid', {
                guid: stockEntry.batch.unit.guid,
              })
              .catch(console.warn);
            if (unitRes?.data?.unit?.name) {
              remainder += ` ${unitRes.data.unit.name}`;
              mercuryCache.set(`unit_${stockEntry.batch.unit.guid}`, unitRes.data.unit.name);
            }
          }
        }

        const packageType = stockEntry?.batch?.packageList?.[0]?.packingType?.guid || 'Не указано';

        //изменил

        let vetDocument = 6;
        if (stockEntry.vetDocument?.[0]?.uuid) {
          const vetDocumentCached = mercuryCache.get(
            `vetDocument_${stockEntry.vetDocument[0].uuid}`
          );
          if (vetDocumentCached) {
            console.log('vetDocumentCached', vetDocumentCached);
            vetDocument = vetDocumentCached.vetDStatus;
          } else {
            const vetDocumentRes = await checkApplicationStatus(
              await $api
                .post('/fgis-soap-native/fgis/api/v1/mercury/getVetDocumentByUuid', {
                  uuid: stockEntry.vetDocument[0]?.uuid,
                  enterpriseGuid: defaultEnterpriseGuid,
                })
                .catch(console.warn)
            );
            if (vetDocumentRes && !vetDocumentRes?.errors?.length) {
              const vetDocumentResult =
                vetDocumentRes?.data?.application?.result?.content?.vetDocument?.vetDStatus;
              mercuryCache.set(`vetDocument_${stockEntry.vetDocument[0].uuid}`, vetDocumentResult);
              vetDocument = vetDocumentResult.vetDStatus;
            }
          }
        } else if (stockEntry.vetEventList?.laboratoryResearch?.[0]?.result) {
          vetDocument = stockEntry.vetEventList?.laboratoryResearch?.[0]?.result;
        }

        const answer: JournalRow = {
          number,
          addDate,
          productionDate: productionDate as Date,
          expiryDate: new Date(),
          creator,
          type,
          productItem,
          volume,
          remainder,
          packageQuantity: '1',
          packageType,
          vetDocument,
          comment: '',
          guid,
          uuid,
          unit,
          packageList: stockEntry.batch.packageList,
        };

        journalRowsAll.push(answer);
      }

      set({
        journalRowsAll,
        enterpriseList: newEnterpriseList,
        businessEntities: newBusinessEntitiesList,
        productItems: newProductItems,
      });
    }

    filterJournal(filterJournalParams);
  },
  addJournalEntrie: async (entrie) => {
    const {
      currentUnit,
      productItems,
      getEnterpriseList,
      getJournalEntries,
      checkApplicationStatus,
    } = get();

    const enterpriseGuid = entrie.creator;
    const productItem = productItems.find((el) => el.guid === entrie.productItem);
    if (!productItem) return showMercuryError('Указанный продукт не найден в базе');
    const tz =
      useUserStore.getState().userInfo?.timezone ??
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    const prodM = moment.utc(entrie.productionDate).tz(tz);
    const expM = moment.utc(entrie.expiryDate).tz(tz);

    const params: AddJournalParams = {
      enterprise: {
        guid: enterpriseGuid,
        // guid: '75a23f8f-05d5-493a-893a-d97cb4de86be'
      },
      productionOperation: {
        // finalizeOperation: true,
        productiveBatch: [
          {
            origin: {
              country: {
                guid: '74a3cbb1-56fa-94f3-ab3f-e8db4940d96b',
              },
              producer: [
                {
                  enterprise: {
                    guid: enterpriseGuid,
                  },
                },
              ],
            },
            productType: productItem.productType,
            product: {
              guid: productItem.product.guid,
            },
            subProduct: {
              guid: productItem.subProduct.guid,
            },
            productItem: {
              guid: productItem.guid,
            },
            volume: getDigFromString(entrie.volume),
            unit: {
              guid: currentUnit?.guid || '21ed96c9-337b-4a27-8761-c6e6ad3c9f5b',
            },
            dateOfProduction: {
              firstDate: {
                year: prodM.year(),
                month: prodM.month() + 1,
                day: prodM.date(),
                hour: prodM.hour(),
              },
            },
            expiryDate: {
              firstDate: {
                year: expM.year(),
                month: expM.month() + 1,
                day: expM.date(),
                hour: expM.hour(),
              },
            },
            perishable: false,
            lowGradeCargo: false,
            packageList: [
              {
                level: 4,
                packingType: {
                  guid: entrie.packageType,
                },
                quantity: entrie.packageQuantity,
              },
            ],
          },
        ],
      },
      vetDocument: [
        {
          authentication: {
            cargoExpertized: entrie.vetDocument,
          },
        },
      ],
    };
    const path = '/fgis-soap-native/fgis/api/v1/mercury/registerProductionOperation';

    const res = await checkApplicationStatus(
      await $api.post(path, params).catch(() => {
        showMercuryError('Неизвестная ошибка, попробуйте позже, пожалуйста');
      })
    );
    if (!res) return;
    const errors = res?.data?.application?.errors;
    if (!errors?.length) {
      toast.success('Запись успешно добавлена! Она отобразится в ближайшее время');
    } else {
      for (let index = 0; index < errors.length; index++) {
        const error: TMercuryError = errors[index];
        if (!error.value.trim()) continue;

        toast.error(error.value);
      }
    }
    getEnterpriseList('');
    getJournalEntries();
  },
  filterJournal: (params) => {
    const {journalRowsAll, enterpriseList, businessEntities, packageTypes, productTypes} = get();

    if (!journalRowsAll.length) return;

    set({
      isJournalLoading: true,
      filterJournalParams: params,
      journalRows: [],
    });

    let filteredRows = journalRowsAll;
    const search = params.search?.trim()?.toLocaleLowerCase();
    if (search) {
      filteredRows = filteredRows.filter((el) => {
        const apiary = businessEntities.find((elem) => elem.guid === el.creator)?.name || '';

        const type = productTypes.find((productType) => productType.guid === el.type)?.name || '';
        const packageType =
          packageTypes.find((packageType) => packageType.guid === el.packageType)?.name || '';
        const productItem = mercuryCache.get(`productItem_${el.productItem}`)?.name || '';

        if (
          `${el.number}`.includes(search) ||
          apiary.toLocaleLowerCase().includes(search) ||
          type.toLocaleLowerCase().includes(search) ||
          productItem.toLocaleLowerCase().includes(search) ||
          packageType.toLocaleLowerCase().includes(search)
        ) {
          return true;
        }

        return false;
      });
    }

    const sort = params.sort || 'DESC';
    const sortBy = params.sortBy;
    switch (sortBy) {
      case 'addDate':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return b.addDate.getTime() - a.addDate.getTime();
          }
          return a.addDate.getTime() - b.addDate.getTime();
        });
        break;
      case 'productionDate':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return b.productionDate.getTime() - a.productionDate.getTime();
          }
          return a.productionDate.getTime() - b.productionDate.getTime();
        });
        break;
      case 'number':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return Number(b.number) - Number(a.number);
          }
          return Number(a.number) - Number(b.number);
        });
        break;
      case 'volume':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return getDigFromString(b.volume) - getDigFromString(a.volume);
          }
          return getDigFromString(a.volume) - getDigFromString(b.volume);
        });
        break;
      case 'remainder':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return getDigFromString(b.remainder || 0) - getDigFromString(a.remainder || 0);
          }
          return getDigFromString(a.remainder || 0) - getDigFromString(b.remainder || 0);
        });
        break;
      case 'creator':
        filteredRows.sort((a, b) => {
          const aCreatorName = mercuryCache.get(`creator_${a.creator}`)?.name || '';
          const bCreatorName = mercuryCache.get(`creator_${b.creator}`)?.name || '';
          if (sort === 'DESC') {
            return bCreatorName.localeCompare(aCreatorName);
          }
          return aCreatorName.localeCompare(bCreatorName);
        });
        break;
      case 'type':
        filteredRows.sort((a, b) => {
          const aTypeName =
            productTypes.find((productType) => productType.guid === a.type)?.name || '';
          const bTypeName =
            productTypes.find((productType) => productType.guid === b.type)?.name || '';

          if (sort === 'DESC') {
            return bTypeName.localeCompare(aTypeName);
          }
          return aTypeName.localeCompare(bTypeName);
        });
        break;
      case 'packageType':
        filteredRows.sort((a, b) => {
          const aPackageType =
            packageTypes.find((packageType) => packageType.guid === a.packageType)?.name || '';
          const bPackageType =
            packageTypes.find((packageType) => packageType.guid === b.packageType)?.name || '';

          if (sort === 'DESC') {
            return bPackageType.localeCompare(aPackageType);
          }
          return aPackageType.localeCompare(bPackageType);
        });
        break;

      default:
        filteredRows.sort((a, b) => {
          return b.addDate.getTime() - a.addDate.getTime();
        });
        break;
    }

    setTimeout(() => {
      set({
        journalRows: filteredRows,
        isJournalLoading: false,
      });
    }, 5);
  },

  transportTypes: TRANSPORT_TYPES,
  countries: [],
  storageMethods: STORAGE_METHODS,
  businessEntities: [],
  businessEntitity: null,
  defaultBusinessEntitity: null,
  waybillTypes: WAYBILL_TYPES,
  countryAutoNumbers: COUNTRY_AUTO_NUMBERS,

  getTransactionEntries: async () => {
    set({
      isTransactionLoading: true,
    });
    const {
      setErrors,
      filterTransactionParams,
      defaultEnterpriseGuid,
      filterTransactions,
      checkApplicationStatus,
    } = get();

    const path = '/fgis-soap-native/fgis/api/v1/mercury/getVetDocumentList';
    const params = {
      listOptions: {
        count: '999',
        offset: '0',
      },
      enterpriseGuid: defaultEnterpriseGuid,
    };

    const res = await checkApplicationStatus(
      await $api.post(path, params).catch(() => {
        showMercuryError();
      })
    );

    if (!res) return;
    console.log('getTransactionEntries', res);

    // const result = res?.data?.application?.result;
    const result = res?.data?.application?.result || DEFAULT_VET_RESULT;
    const errors = res?.data?.application?.errors;

    if (errors?.length && setErrors) {
      for (let index = 0; index < errors.length; index++) {
        const error: TMercuryError = errors[index];
        if (!error.value.trim()) continue;

        toast.error(error.value);
      }
    }

    const vetDocuments: GettedVetDocument[] = result?.content?.vetDocumentList?.vetDocument;
    if (vetDocuments?.length) {
      const transactionsRowsAll: TransactionsRow[] = [];
      const vetDocumentsFiltered = vetDocuments.filter((el) => {
        if (!el.certifiedConsignment) {
          return false;
        }

        return true;
      });
      for (let index = 0; index < vetDocumentsFiltered.length; index++) {
        const vetDocument = vetDocumentsFiltered[index];

        const number = vetDocument?.issueNumber || 'Не указано';
        const tnnNumber = vetDocument?.issueNumber || 'Не указано';
        let tnnDate;
        if (vetDocument?.issueDate) {
          tnnDate = new Date(vetDocument.issueDate);
        }
        const lastUpdateDate = new Date(vetDocument.lastUpdateDate);
        const tnnType = vetDocument?.type || 'Не указано';

        const vetDStatus = vetDocument.vetDStatus;
        let status = TTransactionStatus.PENDING;
        switch (vetDStatus) {
          case 1:
            status = TTransactionStatus.ACTIVE;
            break;
          case 2:
            status = TTransactionStatus.REJECTED;
            break;
          case 3:
            status = TTransactionStatus.CLOSED;
            break;
        }

        let apiary = 'Не указано';
        if (vetDocument?.certifiedConsignment?.consignor?.enterprise?.guid) {
          const apiaryCached = mercuryCache.get(
            `transEnterprise_${vetDocument.certifiedConsignment.consignor.enterprise.guid}`
          );
          if (apiaryCached) {
            apiary = apiaryCached.name;
          } else {
            const enterpriseRes = await $api
              .post('/fgis-soap-native/fgis/api/v1/mercury/enterprise/getEnterpriseByGuid', {
                guid: vetDocument.certifiedConsignment.consignor.enterprise.guid,
              })
              .catch(console.warn);
            if (enterpriseRes) {
              apiary = enterpriseRes.data.enterprise.name;
              mercuryCache.set(
                `transEnterprise_${vetDocument.certifiedConsignment.consignor.enterprise.guid}`,
                enterpriseRes.data.enterprise
              );
            }
          }
        }

        const transportType = vetDocument.certifiedConsignment.transportInfo.transportType;
        const transportNumber =
          vetDocument.certifiedConsignment.transportInfo.transportNumber.vehicleNumber;
        const transportCountry = 'Не указано';
        const storageMethod = vetDocument.certifiedConsignment.transportStorageType;

        let recipientEnterprise = 'Не указано';
        if (vetDocument?.certifiedConsignment?.consignee?.enterprise?.guid) {
          const recipientEnterpriseCached = mercuryCache.get(
            `transEnterprise_${vetDocument.certifiedConsignment.consignee.enterprise.guid}`
          );
          if (recipientEnterpriseCached) {
            recipientEnterprise = recipientEnterpriseCached.name;
          } else {
            const enterpriseRes = await $api
              .post('/fgis-soap-native/fgis/api/v1/mercury/enterprise/getEnterpriseByGuid', {
                guid: vetDocument.certifiedConsignment.consignee.enterprise.guid,
              })
              .catch(console.warn);
            if (enterpriseRes) {
              recipientEnterprise = enterpriseRes.data.enterprise.name;
              mercuryCache.set(
                `transEnterprise_${vetDocument.certifiedConsignment.consignee.enterprise.guid}`,
                enterpriseRes.data.enterprise
              );
            }
          }
        }

        let recipientCompany = 'Не указано';
        if (vetDocument?.certifiedConsignment?.consignee?.businessEntity?.guid) {
          const recipientCompanyCached = mercuryCache.get(
            `transBusinessEntity_${vetDocument.certifiedConsignment.consignee.businessEntity.guid}`
          );
          if (recipientCompanyCached) {
            recipientCompany = recipientCompanyCached.name;
          } else {
            const enterpriseRes = await $api
              .post('/fgis-soap-native/fgis/api/v1/mercury/enterprise/getBusinessEntityByGuid', {
                guid: vetDocument.certifiedConsignment.consignee.businessEntity.guid,
              })
              .catch(console.warn);
            if (enterpriseRes) {
              recipientCompany = enterpriseRes.data.businessEntity.name;
              mercuryCache.set(
                `transBusinessEntity_${vetDocument.certifiedConsignment.consignee.businessEntity.guid}`,
                enterpriseRes.data.businessEntity
              );
            }
          }
        }

        const date = new Date(vetDocument.issueDate);

        let productionVolume = `${vetDocument.certifiedConsignment.batch.volume}`;
        if (vetDocument?.certifiedConsignment?.batch?.unit?.guid) {
          const cachedUnit = mercuryCache.get(
            `unit_${vetDocument.certifiedConsignment.batch.unit.guid}`
          );
          if (cachedUnit) {
            productionVolume += ` ${cachedUnit}`;
          } else {
            const unitRes = await $api
              .post('/fgis-soap-native/fgis/api/v1/mercury/dictionary/getUnitByGuid', {
                guid: vetDocument.certifiedConsignment.batch.unit.guid,
              })
              .catch(console.warn);
            if (unitRes?.data?.unit?.name) {
              productionVolume += ` ${unitRes.data.unit.name}`;
              mercuryCache.set(
                `unit_${vetDocument.certifiedConsignment.batch.unit.guid}`,
                unitRes.data.unit.name
              );
            }
          }
        }

        const answer: TransactionsRow = {
          number,
          status,
          apiary,
          transportType,
          transportCountry,
          transportNumber,
          storageMethod,
          recipientEnterprise,
          recipientCompany,
          tnnNumber,
          tnnDate,
          tnnType,
          date,
          productionVolume,
          lastUpdateDate,
        };

        transactionsRowsAll.push(answer);
      }

      set({
        transactionsRowsAll,
      });
    }

    filterTransactions(filterTransactionParams);
  },
  addTransactionEntrie: async (entrie) => {
    const {getEnterpriseList, checkApplicationStatus, getTransactionEntries} = get();
    const path = '/fgis-soap-native/fgis/api/v1/mercury/prepareOutgoingConsignment';

    const res = await checkApplicationStatus(
      await $api.post(path, entrie).catch(() => {
        showMercuryError('Неизвестная ошибка, попробуйте позже, пожалуйста');
      })
    );
    const errors = res?.data?.application?.errors;
    const result = res?.data?.application?.result;

    if (errors?.length) {
      for (let index = 0; index < errors.length; index++) {
        const error: TMercuryError = errors[index];
        if (!error.value.trim()) continue;

        toast.error(error.value);
      }
    }

    if (result) {
      toast.success('Запись успешно добавлена! Она отобразится в ближайшее время');
    }

    getEnterpriseList('');
    getTransactionEntries();
    // const { transactionsRowsAll, getTransactionEntries } = get();
    // const newTransactionRows = transactionsRowsAll.concat(entrie)
    // const userId = useUserStore.getState().userInfo?.id;
    // const res = await $api.post('/stash', {
    //   userId,
    //   entryId: `mercury_transactions_${userId}`,
    //   data: {
    //     items: newTransactionRows
    //   }
    // }).catch(console.warn)
    // if (!res) toast.error('Произошла ошибка, попробуйте позже, пожалуйста')

    // toast.success('Запись успешно добавлена');
    // await getTransactionEntries();
  },
  filterTransactions: (params) => {
    const {transactionsRowsAll} = get();

    set({
      isTransactionLoading: true,
      filterTransactionParams: params,
      transactionsRows: [],
    });

    let filteredRows = transactionsRowsAll;
    const search = params.search?.trim()?.toLocaleLowerCase();
    if (search) {
      filteredRows = filteredRows.filter((el) => {
        if (
          `${el.number}`.includes(search) ||
          el.recipientEnterprise.toLocaleLowerCase().includes(search) ||
          el.recipientCompany.toLocaleLowerCase().includes(search)
        ) {
          return true;
        }

        return false;
      });
    }

    const sort = params.sort || 'DESC';
    const sortBy = params.sortBy;
    switch (sortBy) {
      case 'date':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return b.lastUpdateDate.getTime() - a.lastUpdateDate.getTime();
          }
          return a.lastUpdateDate.getTime() - b.lastUpdateDate.getTime();
        });
        break;
      case 'number':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return Number(b.number) - Number(a.number);
          }
          return Number(a.number) - Number(b.number);
        });
        break;
      case 'volume':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return getDigFromString(b.productionVolume) - getDigFromString(a.productionVolume);
          }
          return getDigFromString(a.productionVolume) - getDigFromString(b.productionVolume);
        });
        break;
      case 'recipient':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return b.recipientEnterprise.localeCompare(a.recipientEnterprise);
          }
          return a.recipientEnterprise.localeCompare(b.recipientEnterprise);
        });
        break;
      case 'status':
        filteredRows.sort((a, b) => {
          if (sort === 'DESC') {
            return b.status.localeCompare(a.status);
          }
          return a.status.localeCompare(b.status);
        });
        break;

      default:
        filteredRows.sort((a, b) => {
          return b.lastUpdateDate.getTime() - a.lastUpdateDate.getTime();
        });
        break;
    }

    setTimeout(() => {
      set({
        transactionsRows: filteredRows,
        isTransactionLoading: true,
      });
    }, 5);
  },

  updateEnterprise: (enterprise) => {
    set({
      enterprise,
    });
  },
  getEnterpriseList: async (query) => {
    const {enterprise} = get();
    const queryParams = {
      name: query,
    };
    const params: BaseMercuryRequestParams = {
      listOptions: {
        count: '20',
        offset: '0',
      },
      enterprise: queryParams,
    };

    const path = '/fgis-soap-native/fgis/api/v1/mercury/enterprise/getRussianEnterpriseList';
    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;

    const newEnterpriseList = (res.data?.enterpriseList?.enterprise || []).filter(
      (el: TEnterprise) => el.name.length > 1 && el.active
    );
    if (enterprise) {
      newEnterpriseList.unshift(enterprise);
    }

    set({
      enterpriseList: newEnterpriseList,
    });
  },
  getProductByTypeList: async () => {
    const {getSubProductByProductList} = get();

    const params: BaseMercuryRequestParams = {
      listOptions: {
        count: '999',
        offset: '0',
      },
      productType: 5,
    };

    const path = '/fgis-soap-native/fgis/api/v1/mercury/product/getProductByTypeList';
    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;
    const products = res.data.productList.product;
    if (products.length) {
      for (let index = 0; index < products.length; index++) {
        const product = products[index];
        if (
          (!product.name.toLocaleLowerCase().includes('пчел') &&
            !product.name.toLocaleLowerCase().includes('пчёл')) ||
          !product.guid
        )
          continue;
        await getSubProductByProductList(product.guid);
      }
    }
  },
  getSubProductByProductList: async (productGuid = '7d3809aa-983b-62c6-17e5-6d026bb5dfec') => {
    const {productTypes} = get();

    const params: BaseMercuryRequestParams = {
      listOptions: {
        count: '56',
        offset: '0',
      },
      productGuid,
    };

    const path = '/fgis-soap-native/fgis/api/v1/mercury/product/getSubProductByProductList';
    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;
    const subProductList = res.data?.subProductList?.subProduct || [];
    set({
      productTypes: productTypes.concat(subProductList),
    });
  },
  getUnitList: async () => {
    const params: BaseMercuryRequestParams = {
      listOptions: {
        count: '999',
        offset: '0',
      },
    };
    const path = '/fgis-soap-native/fgis/api/v1/mercury/dictionary/getUnitList';
    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;
    const units = res?.data?.unitList?.unit || [];
    for (let index = 0; index < units.length; index++) {
      const unit = units[index];
      if (unit.name.toLocaleLowerCase() === 'кг' || unit.name.toLocaleLowerCase() === 'килограмм') {
        set({
          currentUnit: unit,
        });
      }
    }
  },
  getPurposeList: async () => {
    const params: BaseMercuryRequestParams = {
      listOptions: {
        count: '999',
        offset: '0',
      },
    };
    const path = '/fgis-soap-native/fgis/api/v1/mercury/dictionary/getPurposeList';
    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;
    const purposeList = res?.data?.purposeList?.purpose || [];

    set({
      purposes: purposeList,
    });
  },
  getProductItemList: async (params) => {
    const path = '/fgis-soap-native/fgis/api/v1/mercury/product/getProductItemList';
    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;
    const productItems = res?.data?.productItemList?.productItem || [];
    set({
      productItems,
    });
  },
  getAllCountryList: async () => {
    const path = '/fgis-soap-native/fgis/api/v1/mercury/ikar/getAllCountryList';
    const params = {
      listOptions: {
        count: '999',
        offset: '0',
      },
    };

    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;
    const countries = res?.data?.countryList?.country || [];
    countries.sort((a: TCountry, b: TCountry) => {
      if (a.name.includes('Российская Федерация')) return -1;
      if (b.name.includes('Российская Федерация')) return 1;

      return a.name.localeCompare(b.name);
    });
    set({
      countries,
    });
  },
  getBusinessEntityList: async (query) => {
    const path = '/fgis-soap-native/fgis/api/v1/mercury/enterprise/getBusinessEntityList';
    const queryParams = {
      name: '',
      inn: '',
    };
    if (query.length > 12 || isNaN(Number(query))) {
      queryParams.name = query;
    } else {
      queryParams.inn = query;
    }
    const params = {
      listOptions: {
        count: '20',
        offset: '0',
      },
      businessEntity: queryParams,
    };

    const res = await $api.post(path, params).catch(() => {
      showMercuryError();
    });

    if (!res) return;

    const businessEntities = res?.data?.businessEntityList?.businessEntity || [];
    set({
      businessEntities,
    });
  },
  getDefaultEnterprise: async () => {
    const vetisBusiness = useVetisStore.getState().vetisBusiness;
    const vetisBusinessId = vetisBusiness?.issuerId;
    if (!vetisBusinessId) return;

    const businessEntityPath =
      '/fgis-soap-native/fgis/api/v1/mercury/enterprise/getActivityLocationList';
    const params = {
      listOptions: {
        count: '1',
        offset: '0',
      },
      businessEntity: {
        guid: vetisBusinessId,
      },
    };

    const businessRes = await $api.post(businessEntityPath, params).catch(console.warn);
    const businessEntity = businessRes?.data?.activityLocationList?.location?.[0];
    if (!businessEntity) return;
    set({
      defaultEnterpriseGuid: businessEntity.enterprise.guid,
      enterprise: businessEntity.enterprise,
      defaultEnterprise: businessEntity.enterprise,
    });

    const businessEntityGuid = businessEntity?.businessEntity?.guid;
    if (!businessEntityGuid) return;

    const businessEntityRes = await $api
      .post('/fgis-soap-native/fgis/api/v1/mercury/enterprise/getBusinessEntityByGuid', {
        guid: businessEntityGuid,
      })
      .catch(console.warn);

    const defaultBusinessEntitity = businessEntityRes?.data?.businessEntity;
    if (!defaultBusinessEntitity) return;
    if (!businessEntity) return;
    set({
      defaultBusinessEntitity,
    });
  },

  getMercuryDictionaries: async () => {
    const {
      getEnterpriseList,
      getProductByTypeList,
      getUnitList,
      getPurposeList,
      getAllCountryList,
      getBusinessEntityList,
      getDefaultEnterprise,
    } = get();

    await getDefaultEnterprise();
    await getEnterpriseList('');
    await getProductByTypeList();
    await getUnitList();
    await getPurposeList();
    await getAllCountryList();
    await getBusinessEntityList('');
  },

  checkApplicationStatus: async (res) => {
    if (!res) return res;
    const {checkApplicationStatus, receiveApplicationResult} = get();

    const applicationId = res?.data?.application?.applicationId;
    const status = res?.data?.application?.status;
    if (status === 1 && applicationId) {
      return await checkApplicationStatus(await receiveApplicationResult(applicationId));
    } else {
      return res;
    }
  },
  receiveApplicationResult: async (applicationId) => {
    const path = '/fgis-soap-native/fgis/api/v1/mercury/receiveApplicationResult';
    const params = {
      applicationId,
    };

    const res = await $api.post(path, params);

    return res;
  },

  editJournalEntrie: async (entrie) => {
    const {journalRowsAll, getJournalEntries} = get();
    const newRows = structuredClone?.(journalRowsAll) || JSON.parse(JSON.stringify(journalRowsAll));

    const idx = newRows.findIndex((el) => el.number === entrie.number);
    if (idx >= 0) {
      newRows[idx] = entrie;
    }

    const userId = useUserStore.getState().userInfo?.id;
    const res = await $api
      .post('/stash', {
        userId,
        entryId: `mercury_journal_${userId}`,
        data: {
          items: newRows,
        },
      })
      .catch(console.warn);
    if (!res) toast.error('Произошла ошибка, попробуйте позже, пожалуйста');

    toast.success('Запись успешно изменена');
    await getJournalEntries();
  },
  editTransactionEntrie: async (entrie) => {
    const {transactionsRowsAll, getTransactionEntries} = get();
    const newRows =
      structuredClone?.(transactionsRowsAll) || JSON.parse(JSON.stringify(transactionsRowsAll));

    const idx = newRows.findIndex((el) => el.number === entrie.number);
    if (idx >= 0) {
      newRows[idx] = entrie;
    }

    const userId = useUserStore.getState().userInfo?.id;
    const res = await $api
      .post('/stash', {
        userId,
        entryId: `mercury_transactions_${userId}`,
        data: {
          items: newRows,
        },
      })
      .catch(console.warn);
    if (!res) toast.error('Произошла ошибка, попробуйте позже, пожалуйста');

    toast.success('Транзакция успешно изменена');
    await getTransactionEntries();
  },
  removeDefaultBusinessEntitity: () => {
    set({
      defaultBusinessEntitity: null,
    });
  },
}));
